
(function () {
  "use strict";

  // ====================================================
  // CONFIGURACIÓN
  // ====================================================

  const CONFIG = window.AUTO_GIFT_CONFIG || {};
  const collectionCache = new Map();

  let isSyncingGift = false;
  let syncPending = false;
  let syncTimer = null;

  let giftLinkObserver = null;
  let giftLinkTimer = null;
  let giftLinkUpdating = false;
  let giftLinkPending = false;
  let giftLinkProtectedRoot = null;
  let giftLinkObservedRoot = null;
  let giftRootRetryTimer = null;

  let activeGiftLinkVariants = new Set();
  let activeGiftLinkKeys = new Set();

  let lastCartSignature = null;
  let cartCheckTimer = null;
  let checkingCart = false;
  let cartCheckPending = false;

  // ====================================================
  // UTILIDADES
  // ====================================================

  function cartUrl(path) {
    const root = window.Shopify?.routes?.root || "/";

    return `${
      root.endsWith("/") ? root : root + "/"
    }${path}`;
  }

  function isEnabled() {
    return (
      CONFIG.enabled === true &&
      Array.isArray(CONFIG.gifts)
    );
  }

  function getGiftType(gift) {
    return gift.type || "collection";
  }

  function getGiftQuantityMode(gift) {
    return gift.giftQuantityMode || "fixed";
  }

  function isAutoGift(item) {
    return item.properties?._auto_gift === "true";
  }

  function cartSignature(cart) {
    return JSON.stringify(
      (cart.items || [])
        .map((item) => [
          item.key,
          item.variant_id,
          item.quantity,
          item.final_line_price,
          item.properties?._auto_gift,
          item.properties?._auto_gift_id,
        ])
        .sort((a, b) =>
          String(a[0]).localeCompare(String(b[0]))
        )
    );
  }

  function rememberCart(cart) {
    lastCartSignature = cartSignature(cart);
  }

  // ====================================================
  // PETICIONES INTERNAS
  // ====================================================

  const nativeFetch = window.fetch.bind(window);

  async function internalFetch(url, options = {}) {
    return nativeFetch(url, options);
  }

  async function getCart() {
    const response = await internalFetch(
      cartUrl("cart.js"),
      {
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Error consultando carrito: ${response.status}`
      );
    }

    return response.json();
  }

  // ====================================================
  // VALIDAR CONFIGURACIÓN
  // ====================================================

  function isValidGiftConfig(gift) {
    if (
      !gift ||
      !gift.id ||
      !gift.giftVariantId
    ) {
      return false;
    }

    const quantity = Number(gift.giftQuantity ?? 1);

    if (
      !Number.isInteger(quantity) ||
      quantity <= 0
    ) {
      return false;
    }

    const mode = getGiftQuantityMode(gift);

    if (!["fixed", "per_unit"].includes(mode)) {
      return false;
    }

    const type = getGiftType(gift);

    if (type === "product") {
      const minQuantity = Number(
        gift.minQuantity ?? 1
      );

      return (
        Boolean(
          gift.triggerProductId ||
          gift.triggerVariantId
        ) &&
        Number.isInteger(minQuantity) &&
        minQuantity > 0
      );
    }

    if (type === "collection") {
      return (
        Boolean(gift.collectionHandle) &&
        Number(gift.minCartTotal) > 0 &&
        mode === "fixed"
      );
    }

    return false;
  }

  // ====================================================
  // IDENTIFICACIÓN DE REGALOS
  // ====================================================

  function findGiftItem(cart, giftVariantId) {
    return cart.items.find(
      (item) =>
        String(item.variant_id) ===
        String(giftVariantId)
    );
  }

  function findAutoGiftItems(cart, gift) {
    return cart.items.filter(
      (item) =>
        String(item.variant_id) ===
          String(gift.giftVariantId) &&
        isAutoGift(item) &&
        item.properties?._auto_gift_id === gift.id
    );
  }

  function findAutoGiftItem(cart, gift) {
    return findAutoGiftItems(cart, gift)[0] || null;
  }

  // ====================================================
  // PRODUCTOS DE COLECCIÓN
  // ====================================================

  async function getCollectionProductIds(handle) {
    if (collectionCache.has(handle)) {
      return collectionCache.get(handle);
    }

    const ids = new Set();
    let page = 1;

    while (true) {
      const response = await internalFetch(
        cartUrl(
          `collections/${encodeURIComponent(
            handle
          )}/products.json?limit=250&page=${page}`
        ),
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Error consultando colección: ${handle}`
        );
      }

      const data = await response.json();
      const products = data.products || [];

      for (const product of products) {
        ids.add(String(product.id));
      }

      if (products.length < 250) {
        break;
      }

      page++;
    }

    collectionCache.set(handle, ids);

    return ids;
  }

  // ====================================================
  // CANTIDAD DE PRODUCTOS QUE ACTIVAN PROMOCIÓN
  // ====================================================

  function getTriggerQuantity(cart, gift) {
    return cart.items.reduce((total, item) => {
      if (isAutoGift(item)) {
        return total;
      }

      // Evitar que el propio regalo active la promoción.
      if (
        String(item.variant_id) ===
        String(gift.giftVariantId)
      ) {
        return total;
      }

      const matches = gift.triggerVariantId
        ? String(item.variant_id) ===
          String(gift.triggerVariantId)
        : String(item.product_id) ===
          String(gift.triggerProductId);

      return matches
        ? total + item.quantity
        : total;
    }, 0);
  }

  // ====================================================
  // VALIDAR CONDICIONES DE PROMOCIÓN
  // ====================================================

  async function qualifiesForGift(cart, gift) {
    const type = getGiftType(gift);

    if (type === "product") {
      const minQuantity = Number(
        gift.minQuantity ?? 1
      );

      return (
        getTriggerQuantity(cart, gift) >= minQuantity
      );
    }

    if (type === "collection") {
      const productIds =
        await getCollectionProductIds(
          gift.collectionHandle
        );

      const subtotal = cart.items.reduce(
        (total, item) => {
          if (isAutoGift(item)) {
            return total;
          }

          if (
            String(item.variant_id) ===
            String(gift.giftVariantId)
          ) {
            return total;
          }

          if (
            !productIds.has(
              String(item.product_id)
            )
          ) {
            return total;
          }

          return total + item.final_line_price;
        },
        0
      );

      return (
        subtotal >= Number(gift.minCartTotal)
      );
    }

    return false;
  }

  // ====================================================
  // CALCULAR CANTIDAD ESPERADA DE REGALOS
  // ====================================================

  async function getExpectedGiftQuantity(cart, gift) {
    const qualifies = await qualifiesForGift(
      cart,
      gift
    );

    if (!qualifies) {
      return 0;
    }

    const giftQuantity = Number(
      gift.giftQuantity ?? 1
    );

    const mode = getGiftQuantityMode(gift);

    // Una cantidad fija por promoción.
    if (mode === "fixed") {
      return giftQuantity;
    }

    // Cantidad proporcional a los productos comprados.
    if (
      mode === "per_unit" &&
      getGiftType(gift) === "product"
    ) {
      const minQuantity = Number(
        gift.minQuantity ?? 1
      );

      const triggerQuantity = getTriggerQuantity(
        cart,
        gift
      );

      return (
        Math.floor(triggerQuantity / minQuantity) *
        giftQuantity
      );
    }

    return 0;
  }

  // ====================================================
  // AGREGAR REGALO
  // ====================================================

  async function addGift(gift, quantity = 1) {
    const response = await internalFetch(
      cartUrl("cart/add.js"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: Number(gift.giftVariantId),
          quantity,
          properties: {
            _auto_gift: "true",
            _auto_gift_id: gift.id,
          },
        }),
      }
    );

    if (!response.ok) {
      const error = await response
        .text()
        .catch(() => "");

      throw new Error(
        `Error agregando regalo: ${error}`
      );
    }

    return true;
  }

  // ====================================================
  // ACTUALIZAR CANTIDAD DE REGALO
  // ====================================================

  async function changeGiftQuantity(item, quantity) {
    const response = await internalFetch(
      cartUrl("cart/change.js"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: item.key,
          quantity,
        }),
      }
    );

    if (!response.ok) {
      const error = await response
        .text()
        .catch(() => "");

      throw new Error(
        `Error actualizando regalo: ${error}`
      );
    }

    return true;
  }

  async function removeGift(item) {
    return changeGiftQuantity(item, 0);
  }

  // ====================================================
  // ACTUALIZAR UPCART
  // ====================================================

  async function refreshCart() {
    try {
      if (
        typeof window.upcartRefreshCart ===
        "function"
      ) {
        await window.upcartRefreshCart();
      }
    } catch (error) {
      // Evitar que un error de UpCart interrumpa
      // la sincronización de regalos.
    }
  }

  // ====================================================
  // SINCRONIZACIÓN PRINCIPAL
  // ====================================================

  async function syncAutoGift() {
    if (!isEnabled()) {
      return;
    }

    if (isSyncingGift) {
      syncPending = true;
      return;
    }

    isSyncingGift = true;

    let cartChanged = false;

    try {
      const cart = await getCart();

      rememberCart(cart);

      for (const gift of CONFIG.gifts) {
        if (!isValidGiftConfig(gift)) {
          continue;
        }

        const expectedQuantity =
          await getExpectedGiftQuantity(cart, gift);

        const autoGiftItems =
          findAutoGiftItems(cart, gift);

        const currentQuantity =
          autoGiftItems.reduce(
            (total, item) =>
              total + item.quantity,
            0
          );

        // Si ya no califica, pero está configurado
        // para conservar el regalo, no modificarlo.
        if (
          expectedQuantity === 0 &&
          gift.removeGiftWhenNotQualified === false
        ) {
          continue;
        }

        // Normalizar si existen varias líneas automáticas.
        if (autoGiftItems.length > 1) {
          const extraItem =
            autoGiftItems[autoGiftItems.length - 1];

          cartChanged = await removeGift(extraItem);
          break;
        }

        const autoGift = autoGiftItems[0] || null;

        // Agregar cuando no existe regalo automático.
        if (expectedQuantity > 0 && !autoGift) {
          // Si la variante fue agregada manualmente,
          // no crear otra línea automática.
          const existingManualGift = cart.items.some(
            (item) =>
              String(item.variant_id) ===
                String(gift.giftVariantId) &&
              !isAutoGift(item)
          );

          if (existingManualGift) {
            continue;
          }

          cartChanged = await addGift(
            gift,
            expectedQuantity
          );

          break;
        }

        // Ajustar cantidad cuando cambia la compra.
        if (
          autoGift &&
          currentQuantity !== expectedQuantity
        ) {
          cartChanged = await changeGiftQuantity(
            autoGift,
            expectedQuantity
          );

          break;
        }
      }

      if (cartChanged) {
        const updatedCart = await getCart();

        rememberCart(updatedCart);

        await refreshCart();

        await updateGiftProductLinks();

        scheduleGiftLinkUpdate(300);
      } else {
        await updateGiftProductLinks();
      }
    } catch (error) {
      // La siguiente sincronización volverá a evaluar
      // el estado del carrito.
    } finally {
      isSyncingGift = false;

      if (syncPending) {
        syncPending = false;
        scheduleSync(250);
      } else if (cartChanged) {
        scheduleSync(250);
      }
    }
  }

  function scheduleSync(delay = 250) {
    if (!isEnabled()) {
      return;
    }

    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      syncTimer = null;
      syncAutoGift();
    }, delay);
  }

  // ====================================================
  // DETECTAR CAMBIOS AJAX DEL CARRITO
  // ====================================================

  function isCartMutation(url, method) {
    if (
      method === "GET" ||
      method === "HEAD"
    ) {
      return false;
    }

    try {
      const pathname = new URL(
        url,
        window.location.origin
      ).pathname;

      return /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/.test(
        pathname
      );
    } catch {
      return false;
    }
  }

  function handleExternalCartChange() {
    if (!isEnabled()) {
      return;
    }

    scheduleSync(150);
    scheduleGiftLinkUpdate(300);
  }

  // ====================================================
  // INTERCEPTOR FETCH
  // ====================================================

  function initializeFetchInterceptor() {
    if (window.__autoGiftFetchInstalled) {
      return;
    }

    window.__autoGiftFetchInstalled = true;

    const previousFetch = window.fetch;

    window.fetch = function (...args) {
      const input = args[0];
      const options = args[1] || {};

      const url =
        typeof input === "string" ||
        input instanceof URL
          ? String(input)
          : input?.url || "";

      const method = String(
        options.method ||
        input?.method ||
        "GET"
      ).toUpperCase();

      const shouldWatch = isCartMutation(
        url,
        method
      );

      const promise = previousFetch.apply(
        this,
        args
      );

      if (shouldWatch) {
        Promise.resolve(promise)
          .then((response) => {
            if (response.ok) {
              handleExternalCartChange();
            }
          })
          .catch(() => {
            // Ignorar solicitudes fallidas.
          });
      }

      return promise;
    };
  }

  // ====================================================
  // INTERCEPTOR XMLHttpRequest
  // ====================================================

  function initializeXHRInterceptor() {
    if (window.__autoGiftXHRInstalled) {
      return;
    }

    window.__autoGiftXHRInstalled = true;

    const originalOpen =
      XMLHttpRequest.prototype.open;

    const originalSend =
      XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (
      method,
      url,
      ...args
    ) {
      this.__autoGiftWatch = isCartMutation(
        String(url),
        String(method).toUpperCase()
      );

      return originalOpen.call(
        this,
        method,
        url,
        ...args
      );
    };

    XMLHttpRequest.prototype.send = function (
      ...args
    ) {
      if (this.__autoGiftWatch) {
        this.addEventListener(
          "loadend",
          () => {
            if (
              this.status >= 200 &&
              this.status < 300
            ) {
              handleExternalCartChange();
            }
          },
          { once: true }
        );
      }

      return originalSend.apply(this, args);
    };
  }

  // ====================================================
  // UPCART - ACCESO AL SHADOW DOM
  // ====================================================

  function getUpcartRoot() {
    return (
      window.upcartDocumentOrShadowRoot ||
      document
    );
  }

  function getUpcartPopup() {
    return getUpcartRoot().querySelector(
      "#UpcartPopup"
    );
  }

  // ====================================================
  // UPCART - IDENTIFICAR FILAS
  // ====================================================

  const UPCART_LINK_SELECTOR = [
    ".upcart-product-image-wrapper a",
    ".upcart-product-title-link",
  ].join(", ");

  function getUpcartRow(link) {
    return link.closest(
      ".upcart-product-item"
    );
  }

  function getRowVariantId(row) {
    if (!row) {
      return null;
    }

    const rowId = row.id || "";
    const match = rowId.match(/^(\d+)(?=:|$)/);

    return match ? match[1] : null;
  }

  // ====================================================
  // UPCART - RESTAURAR ENLACES
  // ====================================================

  function restoreGiftLink(link) {
    const original =
      link.dataset.autoGiftOriginalHref;

    if (!original) {
      delete link.dataset.autoGiftBlocked;
      return;
    }

    link.setAttribute("href", original);

    if (
      link.dataset.autoGiftOriginalNoPointer !==
      undefined
    ) {
      link.setAttribute(
        "data-no-pointer",
        link.dataset.autoGiftOriginalNoPointer
      );
    } else {
      link.removeAttribute(
        "data-no-pointer"
      );
    }

    link.style.removeProperty(
      "pointer-events"
    );

    link.style.removeProperty("cursor");

    delete link.dataset.autoGiftOriginalHref;
    delete link.dataset.autoGiftOriginalNoPointer;
    delete link.dataset.autoGiftBlocked;
  }

  // ====================================================
  // UPCART - DESACTIVAR ENLACES
  // ====================================================

  function disableGiftLink(link) {
    const href = link.getAttribute("href");

    if (
      href &&
      !link.dataset.autoGiftOriginalHref
    ) {
      link.dataset.autoGiftOriginalHref = href;
    }

    if (
      link.dataset.autoGiftOriginalNoPointer ===
        undefined &&
      link.hasAttribute("data-no-pointer")
    ) {
      link.dataset.autoGiftOriginalNoPointer =
        link.getAttribute("data-no-pointer");
    }

    if (link.hasAttribute("href")) {
      link.removeAttribute("href");
    }

    link.setAttribute(
      "data-no-pointer",
      "true"
    );

    link.style.pointerEvents = "none";
    link.style.cursor = "default";

    link.dataset.autoGiftBlocked = "true";
  }

  // ====================================================
  // UPCART - IDENTIFICAR REGALOS ACTIVOS
  // ====================================================

  function getActiveGiftVariants(cart) {
    const variants = new Set();
    const keys = new Set();

    if (!isEnabled()) {
      return { variants, keys };
    }

    for (const gift of CONFIG.gifts) {
      if (
        gift.disableGiftProductLink !== true ||
        !gift.giftVariantId
      ) {
        continue;
      }

      const items = findAutoGiftItems(
        cart,
        gift
      );

      for (const item of items) {
        variants.add(
          String(item.variant_id)
        );

        keys.add(
          String(item.key)
        );
      }
    }

    return { variants, keys };
  }

  // ====================================================
  // UPCART - ACTUALIZAR ENLACES
  // ====================================================

  async function updateGiftProductLinks() {
    if (giftLinkUpdating) {
      giftLinkPending = true;
      return;
    }

    giftLinkUpdating = true;

    try {
      const cart = await getCart();

      const { variants, keys } =
        getActiveGiftVariants(cart);

      activeGiftLinkVariants = variants;
      activeGiftLinkKeys = keys;

      const popup = getUpcartPopup();

      if (!popup) {
        return;
      }

      const rows = popup.querySelectorAll(
        ".upcart-product-item"
      );

      rows.forEach((row) => {
        const variantId = getRowVariantId(row);

        const rowId = String(row.id || "");

        const rowKey = rowId.includes(":")
          ? rowId
          : null;

        const isGift =
          (rowKey && keys.has(rowKey)) ||
          (
            !rowKey &&
            variants.has(variantId)
          );

        const links = row.querySelectorAll(
          UPCART_LINK_SELECTOR
        );

        links.forEach((link) => {
          if (isGift) {
            disableGiftLink(link);
          } else {
            restoreGiftLink(link);
          }
        });
      });
    } catch (error) {
      // Evitar que errores visuales afecten el carrito.
    } finally {
      giftLinkUpdating = false;

      if (giftLinkPending) {
        giftLinkPending = false;
        scheduleGiftLinkUpdate(250);
      }
    }
  }

  function scheduleGiftLinkUpdate(delay = 250) {
    clearTimeout(giftLinkTimer);

    giftLinkTimer = setTimeout(() => {
      giftLinkTimer = null;
      updateGiftProductLinks();
    }, delay);
  }

  // ====================================================
  // UPCART - OBSERVADOR DE CAMBIOS
  // ====================================================

  function initializeGiftLinkObserver() {
    const root = getUpcartRoot();

    if (!root || root === document) {
      return;
    }

    if (giftLinkObservedRoot === root) {
      return;
    }

    if (giftLinkObserver) {
      giftLinkObserver.disconnect();
    }

    giftLinkObservedRoot = root;

    giftLinkObserver = new MutationObserver(
      (mutations) => {
        const hasChanges = mutations.some(
          (mutation) =>
            mutation.type === "childList"
        );

        if (!hasChanges) {
          return;
        }

        scheduleGiftLinkUpdate(150);

        if (isEnabled()) {
          scheduleCartChangeCheck(300);
        }
      }
    );

    giftLinkObserver.observe(root, {
      childList: true,
      subtree: true,
    });

    scheduleGiftLinkUpdate(200);
  }

  // ====================================================
  // UPCART - PROTECCIÓN CONTRA NAVEGACIÓN
  // ====================================================

  function initializeGiftLinkProtection() {
    const root = getUpcartRoot();

    if (!root || root === document) {
      return;
    }

    if (giftLinkProtectedRoot === root) {
      return;
    }

    giftLinkProtectedRoot = root;

    function blockNavigation(event) {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const link = target.closest(
        UPCART_LINK_SELECTOR
      );

      if (!link) {
        return;
      }

      const row = getUpcartRow(link);

      if (!row) {
        return;
      }

      const variantId = getRowVariantId(row);
      const rowKey = String(row.id || "");

      const isGift =
        activeGiftLinkKeys.has(rowKey) ||
        (
          !rowKey.includes(":") &&
          activeGiftLinkVariants.has(variantId)
        );

      if (!isGift) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    root.addEventListener(
      "click",
      blockNavigation,
      true
    );

    root.addEventListener(
      "auxclick",
      blockNavigation,
      true
    );

    root.addEventListener(
      "contextmenu",
      blockNavigation,
      true
    );

    root.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Enter") {
          blockNavigation(event);
        }
      },
      true
    );
  }

  // ====================================================
  // UPCART - INICIALIZACIÓN TARDÍA
  // ====================================================

  function initializeUpcartIntegration() {
    const root = getUpcartRoot();

    if (!root || root === document) {
      clearTimeout(giftRootRetryTimer);

      giftRootRetryTimer = setTimeout(
        initializeUpcartIntegration,
        500
      );

      return;
    }

    clearTimeout(giftRootRetryTimer);
    giftRootRetryTimer = null;

    initializeGiftLinkObserver();
    initializeGiftLinkProtection();

    scheduleGiftLinkUpdate(100);
  }

  // ====================================================
  // COMPROBAR CAMBIOS REALES DEL CARRITO
  // ====================================================

  function scheduleCartChangeCheck(delay = 300) {
    if (!isEnabled()) {
      return;
    }

    clearTimeout(cartCheckTimer);

    cartCheckTimer = setTimeout(() => {
      cartCheckTimer = null;
      checkCartChange();
    }, delay);
  }

  async function checkCartChange() {
    if (!isEnabled()) {
      return;
    }

    if (checkingCart || isSyncingGift) {
      cartCheckPending = true;
      return;
    }

    checkingCart = true;

    try {
      const cart = await getCart();
      const signature = cartSignature(cart);

      if (
        lastCartSignature !== null &&
        signature !== lastCartSignature
      ) {
        rememberCart(cart);
        scheduleSync(100);
      } else if (
        lastCartSignature === null
      ) {
        rememberCart(cart);
      }
    } catch (error) {
      // Se comprobará nuevamente en el siguiente cambio.
    } finally {
      checkingCart = false;

      if (cartCheckPending) {
        cartCheckPending = false;
        scheduleCartChangeCheck(250);
      }
    }
  }

  // ====================================================
  // EVENTOS DEL TEMA
  // ====================================================

  function initializeThemeEvents() {
    document.addEventListener(
      "submit",
      (event) => {
        const form = event.target;

        if (
          form instanceof Element &&
          form.matches(
            'form[action*="/cart/add"]'
          )
        ) {
          scheduleSync(650);
        }
      }
    );

    document.addEventListener(
      "change",
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        if (
          target.matches(
            'input[name="updates[]"], ' +
            'input[name^="updates"], ' +
            'select[name="updates[]"]'
          )
        ) {
          scheduleSync(450);
        }
      }
    );

    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) {
          return;
        }

        const button = target.closest(
          '[data-cart-remove], ' +
          '[data-cart-update], ' +
          '[name="minus"], ' +
          '[name="plus"]'
        );

        if (button) {
          scheduleSync(500);
        }
      }
    );

    for (const eventName of [
      "cart:updated",
      "cart:refresh",
    ]) {
      document.documentElement.addEventListener(
        eventName,
        (event) => {
          if (
            event.detail?.source ===
            "auto-gift"
          ) {
            return;
          }

          scheduleSync(200);
          scheduleGiftLinkUpdate(250);
        }
      );
    }
  }

  // ====================================================
  // INICIALIZACIÓN
  // ====================================================

  function initializeAutoGift() {
    initializeFetchInterceptor();
    initializeXHRInterceptor();
    initializeThemeEvents();

    // UpCart utiliza Shadow DOM.
    initializeUpcartIntegration();

    if (isEnabled()) {
      scheduleSync(100);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      initializeAutoGift,
      { once: true }
    );
  } else {
    initializeAutoGift();
  }
})();
